import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useLocation, useParams } from "react-router";
import ReactTable, { RowInfo } from "react-table";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import {
    ProjectDiscussionsBreadcrumbs,
    TrialProjectDiscussionsBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import * as DiscussionActions from "../../../actions/discussion";
import * as NavigationActions from "../../../actions/navigation";
import * as ProjectActions from "../../../actions/project";
import * as TrialActions from "../../../actions/trial";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
} from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    convertToApproximateDateString,
    convertToShortDateString,
} from "../../../helpers/date";
import {
    ProjectSubMenu,
    TrialProjectSubMenu,
} from "../../../helpers/subMenuHelper";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { ProjectLayout } from "../../index";

import "./DiscussionsPage.scss";

interface DiscussionsPageProps {
    trial: Dtos.Trial;
    formFilter: IFormFilter;
    project: Dtos.Project;
    discussions: Dtos.Discussion[];
    loadingTrial: boolean;
    loadTrialSuccess: boolean;
    loadTrialFailure: boolean;
    loadTrialErrorCode: string;
    loadingProject: boolean;
    loadProjectSuccess: boolean;
    loadProjectFailure: boolean;
    loadProjectErrorCode: string;
    loadingDiscussions: boolean;
    loadDiscussionsSuccess: boolean;
    loadDiscussionsFailure: boolean;
    loadDiscussionsErrorCode: string;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    lookups: Dtos.Lookup[];
    clearProject: ProjectActions.IProjectClearActionCreator;
    loadProject: ProjectActions.IProjectLoadByIdActionCreator;
    loadDiscussions: DiscussionActions.IDiscussionLoadActionCreator;
    clearDiscussions: DiscussionActions.IDiscussionClearActionCreator;
    filter: DiscussionActions.IDiscussionFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    clearTrial: TrialActions.ITrialClearActionCreator;
    loadTrial: TrialActions.ITrialLoadByIdActionCreator;
}

const DiscussionsPage: FC<DiscussionsPageProps> = ({
    trial,
    formFilter,
    project,
    discussions,
    loadTrialFailure,
    loadTrialErrorCode,
    loadingProject,
    loadProjectSuccess,
    loadProjectFailure,
    loadProjectErrorCode,
    loadingDiscussions,
    loadDiscussionsSuccess,
    loadDiscussionsFailure,
    loadDiscussionsErrorCode,
    permissions,
    clearProject,
    loadProject,
    loadDiscussions,
    clearDiscussions,
    filter,
    setBreadcrumbs,
    setDrawerItems,
    clearTrial,
    loadTrial
}) => {
    //
    // -- Params --
    const { projectId } = useParams<{ projectId: string }>();
    const projectIdNumber = parseInt(projectId, 10);

    //
    // -- Hooks --
    const location = useLocation();
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);
    const [updatedBreadcrumbs, setUpdatedBreadcrumbs] =
        useState<boolean>(false);

    //
    // -- Variables --
    const showDiscussions =
        !loadingProject &&
        loadProjectSuccess &&
        !loadingDiscussions &&
        loadDiscussionsSuccess;


    // Load Discussions, project and trial
    useEffect(() => {
        if (projectIdNumber && !initalised) {
            const baseRoute = location?.pathname?.split("/")[1];

            if (baseRoute === "trials" && !trial) {
                setDrawerItems(
                    TrialProjectSubMenu(projectIdNumber, permissions)
                );
                setBreadcrumbs(
                    TrialProjectDiscussionsBreadcrumbs(projectIdNumber)
                );

                loadTrial(projectIdNumber);
            } else if (baseRoute === "projects") {
                setDrawerItems(ProjectSubMenu(projectIdNumber, permissions));
                setBreadcrumbs(ProjectDiscussionsBreadcrumbs(projectIdNumber));
            }

            loadProject(projectIdNumber);
            loadDiscussions(projectIdNumber);
            setInitalised(true);
        }
    }, [
        projectIdNumber,
        location,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        TrialProjectSubMenu,
        TrialProjectDiscussionsBreadcrumbs,
        loadTrial,
        ProjectSubMenu,
        ProjectDiscussionsBreadcrumbs,
        loadProject,
        loadDiscussions,
        initalised,
        setInitalised,
        trial,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadProjectFailure) {
            navigate(`/error/${loadProjectErrorCode}`);
        }
        if (loadTrialFailure) {
            navigate(`/error/${loadTrialErrorCode}`);
        }
        if (loadDiscussionsFailure) {
            navigate(`/error/${loadDiscussionsErrorCode}`);
        }
    }, [
        loadProjectFailure,
        loadTrialFailure,
        loadDiscussionsFailure,
        loadProjectErrorCode,
        loadTrialErrorCode,
        loadDiscussionsErrorCode,
        navigate,
    ]);

    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];

        if (
            baseRoute === "trials" &&
            trial?.displayName &&
            trial?.projectProtocolID &&
            !updatedBreadcrumbs
        ) {
            setDrawerItems(TrialProjectSubMenu(projectIdNumber, permissions));
            setBreadcrumbs(
                TrialProjectDiscussionsBreadcrumbs(
                    projectIdNumber,
                    trial.displayName,
                    trial.projectProtocolID
                )
            );
            setUpdatedBreadcrumbs(true);
        } else if (
            baseRoute === "projects" &&
            project?.projectShortTitle &&
            !updatedBreadcrumbs
        ) {
            setDrawerItems(ProjectSubMenu(projectIdNumber, permissions));
            setBreadcrumbs(
                ProjectDiscussionsBreadcrumbs(
                    projectIdNumber,
                    project.projectShortTitle
                )
            );
            setUpdatedBreadcrumbs(true);
        }
    }, [
        location,
        trial,
        projectIdNumber,
        project,
        permissions,
        updatedBreadcrumbs,
        setDrawerItems,
        setBreadcrumbs,
        TrialProjectSubMenu,
        TrialProjectDiscussionsBreadcrumbs,
        ProjectSubMenu,
        ProjectDiscussionsBreadcrumbs,
        setUpdatedBreadcrumbs,
    ]);

    // Cleanup effect to clear the discussions, project and trial on component unmount
    useEffect(() => {
        return () => {
            clearDiscussions();
            clearProject();
            clearTrial();
        };
    }, [clearDiscussions, clearProject, clearTrial]);

    const createTable = useMemo(() => {
        if (!discussions || discussions.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>There are currently no discussions</span>
                    </div>
                </div>
            );
        }

        const showPagination = discussions.length > DEFAULT_PAGE_SIZE;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={discussions}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? DEFAULT_PAGE_SIZE : discussions.length
                    }
                    pageSize={showPagination ? undefined : discussions.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no meetings to display"
                    resizable={false}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const baseRoute =
                                    location?.pathname?.split("/")[1];

                                const url: string =
                                    baseRoute === "trials"
                                        ? `/trials/${rowInfo.original.trialId}/project/discussions/${rowInfo.original.id}/details`
                                        : `/projects/${rowInfo.original.trialId}/discussions/${rowInfo.original.id}/details`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "id",
                            desc: true,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    className="icon"
                                    icon={FontAwesomeIcons.Duotone.CALENDARALT}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "committee",
                            Header: "Committee",
                            accessor: "committee.name",
                        },
                        {
                            id: "meetingDate",
                            Header: "Meeting Date",
                            accessor: "committeeMeeting.meetingDate",
                            maxWidth: 180,
                            Cell: (props) => (
                                <div>
                                    <span
                                        data-tip={convertToApproximateDateString(
                                            props.value
                                        )}
                                    >
                                        {convertToShortDateString(props.value)}
                                    </span>
                                    <ReactTooltip />
                                </div>
                            ),
                        },
                        {
                            id: "meetingType",
                            Header: "Meeting Type",
                            accessor:
                                "committeeMeeting.committeeMeetingType.value",
                        },
                        {
                            id: "outcome",
                            Header: "Outcome",
                            accessor: "outcome.value",
                        },
                    ]}
                />
            </div>
        );
    }, [discussions, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE, navigate]);

    const renderContent = useMemo(() => {
        const baseRoute = location?.pathname?.split("/")[1];
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Discussions</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />
                        {(AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.ProjectCreate,
                            projectIdNumber,
                            null
                        ) &&
                            project &&
                            project.id) > 0 &&
                            baseRoute === "projects" && (
                                <Link
                                    className={"btn btn-primary"}
                                    url={`/projects/${project?.id}/discussions/create`}
                                >
                                    Add Discussion
                                </Link>
                            )}
                    </div>
                </div>
                {!showDiscussions ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showDiscussions ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        formFilter,
        filter,
        projectIdNumber,
        project,
        showDiscussions,
        createTable,
    ]);

    return (
        <ProjectLayout
            loading={loadingProject || loadingDiscussions}
            permission={Dtos.Permission.ProjectDiscussionView}
        >
            {renderContent}
        </ProjectLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<DiscussionsPageProps> => {
    let project: Dtos.Project | undefined = !(
        state.projects.formData instanceof Array
    )
        ? state.projects.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];
    let trial: Dtos.Trial | undefined = !(
        state.trials.formData instanceof Array
    )
        ? state.trials.formData
        : undefined;

    return {
        formFilter: state.discussions.formFilter,
        project: project!,
        trial: trial!,
        discussions: state.discussions.formFilteredData,
        lookups: state.discussions.lookups,
        loadingProject:
            state.projects.loadState &&
            state.projects.loadState.status == RequestState.Pending,
        loadProjectSuccess:
            state.projects.loadState &&
            state.projects.loadState.status == RequestState.Success,
        loadProjectFailure:
            state.projects.loadState &&
            state.projects.loadState.status == RequestState.Failure,
        loadProjectErrorCode: state.projects.loadState.errorCode,
        loadingDiscussions:
            state.discussions.loadState &&
            state.discussions.loadState.status == RequestState.Pending,
        loadDiscussionsSuccess:
            state.discussions.loadState &&
            state.discussions.loadState.status == RequestState.Success,
        loadDiscussionsFailure:
            state.discussions.loadState &&
            state.discussions.loadState.status == RequestState.Failure,
        loadDiscussionsErrorCode: state.discussions.loadState.errorCode,
        validationFailures: state.discussions.validationFailures,
        permissions: permissions,
    };
};

const mapDispatchToProps = (dispatch): Partial<DiscussionsPageProps> => {
    return {
        filter: bindActionCreators(DiscussionActions.Filter, dispatch),
        loadDiscussions: bindActionCreators(
            DiscussionActions.LoadDiscussion,
            dispatch
        ),
        clearDiscussions: bindActionCreators(DiscussionActions.Clear, dispatch),
        loadProject: bindActionCreators(
            ProjectActions.LoadProjectById,
            dispatch
        ),
        clearProject: bindActionCreators(ProjectActions.Clear, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        loadTrial: bindActionCreators(TrialActions.LoadTrialById, dispatch),
        clearTrial: bindActionCreators(TrialActions.Clear, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(DiscussionsPage);
